import type { ImportStructure } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { NumberField } from '#client/components/ui/NumberField';
import { Select } from '#client/components/ui/Select';
import { formatCount } from '#client/lib/format';
import { UNMAPPED, buildColumnOptions, columnLetter } from '../mapping.logic';
import { SheetPreview } from '../SheetPreview';
import type { ImportRunState } from '../useImportRun';
import { MapColumnsCard } from './MapColumnsCard';
import { MapOptionsCard } from './MapOptionsCard';

type OnMappingChange = ImportRunState['updateMapping'];

const TABLE_COUNT_OPTIONS = [1, 2, 3, 4].map((count) => ({
  value: String(count),
  label: count === 1 ? 'One table' : `${count} tables across the page`,
}));

/**
 * Step two: which sheet, which row is the header, and what each column means.
 *
 * Everything here is a *correction* to something already detected. That is the
 * whole design: the header row, the number of side-by-side tables and the
 * column mapping are all guessed on upload and all shown as editable controls,
 * because auto-detection you cannot correct is worse than none — and their rate
 * list is exactly the sheet that defeats a detector, with two tables, a spacer
 * column and department names used as separators.
 *
 * Every change re-reads the stored workbook, so nothing here requires
 * re-uploading the file.
 */
export function MapStep({ state }: { state: ImportRunState }) {
  const { structure, isBusy, updateMapping } = state;
  if (!structure) return null;

  return (
    <div className="c-form-stack">
      <SheetCard structure={structure} isBusy={isBusy} onChange={updateMapping} />
      <SheetPreview structure={structure} />
      <MapColumnsCard structure={structure} isBusy={isBusy} onChange={updateMapping} />
      <MapOptionsCard run={structure.run} isBusy={isBusy} onChange={updateMapping} />
    </div>
  );
}

/** Which worksheet, where its header is, and how its tables are laid out. */
function SheetCard({
  structure,
  isBusy,
  onChange,
}: {
  structure: ImportStructure;
  isBusy: boolean;
  onChange: OnMappingChange;
}) {
  const { run } = structure;

  return (
    <Card title="The sheet" subtitle="Read from the file you uploaded — change any of it freely">
      <div className="c-form-grid">
        <Select
          label="Worksheet"
          options={run.sheet_names.map((sheet) => ({
            value: sheet.name,
            label: `${sheet.name} — ${formatCount(sheet.rows)} rows`,
          }))}
          value={run.sheet_name}
          onChange={(choice) => void onChange({ sheet_name: choice })}
          disabled={isBusy}
          helpText="Changing this re-reads the file and starts the mapping again"
        />

        <NumberField
          label="Header row"
          min={1}
          value={run.header_row}
          onChange={(row) => {
            if (row >= 1) void onChange({ header_row: row });
          }}
          disabled={isBusy}
          helpText="The row holding the column names, as Excel numbers it"
        />

        <Select
          label="Side-by-side tables"
          options={TABLE_COUNT_OPTIONS}
          value={String(run.block_offsets.length)}
          onChange={(choice) => void onChange({ block_count: Number(choice) })}
          disabled={isBusy}
          helpText="A rate list printed in two columns is two tables, not one"
        />

        <Select
          label="Heading column"
          options={buildColumnOptions(structure.headers, 'No heading rows')}
          value={run.group_column === null ? UNMAPPED : String(run.group_column)}
          onChange={(choice) =>
            void onChange({
              group_column: choice === UNMAPPED ? null : Number(choice),
            })
          }
          disabled={isBusy}
          helpText="Where a lone value — SEROLOGY — is a heading for the rows beneath it"
        />
      </div>

      {run.block_offsets.length > 1 && (
        <p className="c-field__help">
          Reading {run.block_offsets.length} tables of {run.block_width} columns, starting at{' '}
          {run.block_offsets.map((offset) => columnLetter(offset)).join(', ')}.
        </p>
      )}
    </Card>
  );
}
